import React from 'react';
import { motion } from 'framer-motion';
import { EnrichedRiskArea } from '../../types/prevention';
import { RISK_LEVEL_CONFIG, STATUS_CONFIG, getRiskBand, FACTOR_CONFIG, FACTOR_SEVERITY_COLORS, RiskFactorScores, FactorSeverity } from '../RiskAreasWidget';
import { ChevronRight, MapPin, Users, AlertTriangle, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { SifBadge, DataConfidenceBadge, ScheduledDateBadge, hasDataGap } from './shared';
interface Props {
  areas: EnrichedRiskArea[];
  onSelect: (area: EnrichedRiskArea) => void;
}
export function VariantCCards({
  areas,
  onSelect
}: Props) {
  if (areas.length === 0) return <div className="flex flex-col items-center justify-center h-full text-center py-16" data-id="element-2884" data-mp="3ta9z398c95iu">
        <CheckCircle2 size={48} className="text-emerald-400 mb-4" data-id="element-2885" data-mp="22xkwhcq641zx" />
        <p className="text-lg font-medium text-gray-900" data-id="element-2886" data-mp="22xukiurnjz2z">No activities found</p>
      </div>;
  return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-6" data-id="element-2887" data-mp="1yy2aedmm1kvi">
      {areas.map((area, index) => {
      const config = RISK_LEVEL_CONFIG[area.riskLevel];
      const statusConfig = STATUS_CONFIG[area.status];
      const StatusIcon = statusConfig.icon;
      const band = getRiskBand(area.riskScore);
      const activeFactors = (Object.entries(area.riskFactorScores) as [keyof RiskFactorScores, FactorSeverity][]).filter(([_, severity]) => severity !== 'none').sort((a, b) => {
        const order = {
          high: 0,
          medium: 1,
          low: 2,
          none: 3
        };
        return order[a[1]] - order[b[1]];
      });
      const dataGap = hasDataGap(area);
      let cardClass = 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md';
      if (area.sifPotential) {
        cardClass = 'bg-red-50/60 border-red-300 hover:border-red-400 hover:shadow-md border-l-4 border-l-red-700';
      } else if (dataGap) {
        cardClass = 'bg-amber-50/30 border-dashed border-amber-300 hover:border-amber-400 hover:shadow-md border-l-4 border-l-amber-400';
      } else if (area.status === 'closed') {
        cardClass = 'bg-emerald-50/40 border-emerald-200 opacity-75';
      } else if (area.status === 'measures_taken') {
        cardClass = 'bg-amber-50/60 border-amber-200 hover:border-amber-300 hover:shadow-md';
      }
      return <motion.button key={area.id} initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.03
      }} onClick={() => onSelect(area)} className={`w-full text-left p-4 rounded-xl border transition-all group relative flex flex-col ${cardClass}`} data-id="element-2888" data-mp="1stsojb79bdkb" data-mpi={index}>
            {/* Top Right Badges */}
            <div className="absolute top-3 right-3 flex items-center gap-2" data-id="element-2889" data-mp="2v7d8tq7jrlog">
              <DataConfidenceBadge confidence={area.dataConfidence} data-id="element-2890" data-mp="2z7fcu60r8r6w" />
              {area.sifPotential && <SifBadge data-id="element-2891" data-mp="2l9h0f65zv4u1" />}
              {area.status === 'closed' && <CheckCircle2 size={16} className="text-emerald-500" data-id="element-2892" data-mp="2q7ijvs7c1661" />}
              {area.status === 'measures_taken' && <Clock size={16} className="text-amber-500" data-id="element-2893" data-mp="2bi2vgiv9xcti" />}
            </div>

            {/* Top row: band + badges */}
            <div className="flex items-center gap-2 mb-3" data-id="element-2894" data-mp="2gbfjorw1jhlq">
              <div className={`px-2 py-1 rounded ${config.bgColor}`} data-id="element-2895" data-mp="22jhf8ud9y9vw">
                <span className={`text-[10px] font-bold uppercase tracking-wide ${config.color}`} data-id="element-2896" data-mp="27grfl527c5yi">
                  {band.short} · {area.riskScore}
                </span>
              </div>
              <span className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${statusConfig.bgColor} ${statusConfig.color}`} data-id="element-2897" data-mp="1xhs3tdm53u7a">
                <StatusIcon size={10} data-id="element-2898" data-mp="2vunxyxe386u7" />
                {statusConfig.label}
              </span>
            </div>

            {/* Location */}
            <div className="flex items-center gap-1 text-sm font-semibold text-gray-900 mb-1 pr-16" data-id="element-2899" data-mp="2r24jly7kt7oc">
              <MapPin size={14} className="text-gray-400 flex-shrink-0" data-id="element-2900" data-mp="2m30af7knj9y0" />
              <span className="truncate" data-id="element-2901" data-mp="2h6t77mjn4cjr">{area.project}</span>
              <ChevronRight size={12} className="text-gray-300 flex-shrink-0" data-id="element-2902" data-mp="2d3wsw814v2lm" />
              <span className="truncate text-gray-600 font-medium" data-id="element-2903" data-mp="285br4468gav2">
                {area.floor}
              </span>
            </div>
            <div className="flex items-center justify-between mb-3" data-id="element-2904" data-mp="2371r9g9kcm34">
              <p className="text-xs text-gray-500 truncate pr-2" data-id="element-2905" data-mp="6dq9poc20zoc">
                {area.location}
              </p>
              <ScheduledDateBadge date={area.scheduledDate} data-id="element-2906" data-mp="1edqreob0cr1" />
            </div>

            {/* Factors */}
            <div className="flex flex-wrap gap-1.5 pt-3 border-t border-gray-100 mt-auto" data-id="element-2907" data-mp="8z6e2ruzuaep">
              {activeFactors.map(([factor, severity]) => {
            const factorConf = FACTOR_CONFIG[factor];
            const sevColors = FACTOR_SEVERITY_COLORS[severity];
            return <div key={factor} title={factorConf.description} className={`flex items-center gap-1 px-2 py-0.5 rounded-full cursor-help ${sevColors.bg}`} data-id="element-2908" data-mp="nm9b6xfemh1i">
                    <span className={`w-1.5 h-1.5 rounded-full ${sevColors.dot}`} data-id="element-2909" data-mp="3r63xh81q7ga8" />
                    <span className={`text-[10px] font-medium ${sevColors.text}`} data-id="element-2910" data-mp="4wt6rz0lm4ft">
                      {factorConf.label}
                    </span>
                  </div>;
          })}
            </div>
          </motion.button>;
    })}
    </div>;
}